<template>
  <VDialog v-model:visible="visible" class="dialog-support" placement="left" :title="$t('help')" width="40%">
    <VGrid>
      <VCol :md="8" :span="12">
        <div class="icon-button" @click="openExternalAndTrackAPM('product-documentation')">
          <VIcon icon="File" :size="64" type="primary" />
          {{ $t('documentation') }}
        </div>
      </VCol>

      <VCol :md="8" :span="12">
        <div class="icon-button" @click="showLivechat()">
          <VIcon icon="Feedback" :size="64" type="primary" />
          {{ $t('live-chat') }}
        </div>
      </VCol>

      <VCol :md="8" :span="12">
        <div class="icon-button" @click="openExternalAndTrackAPM('support')">
          <VIcon icon="Mail" :size="64" type="primary" />
          {{ $t('mail-us') }}
        </div>
      </VCol>
    </VGrid>

    <div class="title-large mt-15" style="margin-bottom: 10px">{{ $t('have-feedback') }}</div>

    <VTableKeyValue value-width="25px">
      <VTableKeyValueRow @click="dialogs.data = true">
        <template #key>
          <div class="title-small">{{ $t('data') }}</div>
          {{ $t('when-data-is-incorrect-missing-or-outdated') }}
        </template>
        <div class="flex-center">
          <VButton icon="ChevronRight" :icon-size="20" type="link" />
        </div>
      </VTableKeyValueRow>
      <VTableKeyValueRow @click="dialogs.malfunction = true">
        <template #key>
          <div class="title-small">{{ $t('malfunction') }}</div>
          {{ $t('when-something-isnt-working') }}
        </template>
        <div class="flex-center">
          <VButton icon="ChevronRight" :icon-size="20" type="link" />
        </div>
      </VTableKeyValueRow>
      <VTableKeyValueRow @click="openFeatureRequests()">
        <template #key>
          <div class="title-small">{{ $t('feature') }}</div>
          {{ $t('when-you-have-an-idea-to-improve-pascal') }}
        </template>
        <div class="flex-center">
          <VButton icon="ChevronRight" :icon-size="20" type="link" />
        </div>
      </VTableKeyValueRow>
    </VTableKeyValue>

    <template #footer>
      <VButton style="float: left" type="link" @click="openExternalAndTrackAPM('whats-new')">{{ $env.app_name }} {{ $env.version }}</VButton>
      <VButton type="link" @click="openStatusPage">{{ $t('system-status') }}</VButton>
    </template>
  </VDialog>

  <DialogMalfunctionReport v-model:visible="dialogs.malfunction" />
  <DialogDataRequest v-model:visible="dialogs.data" />
</template>

<script setup lang="ts">
const visible = defineModel<boolean>('visible')

const bus = useBus()
const livechat = useLivechat({ loadOnMounted: stores.settings.settings.show_live_chat })

const dialogs = reactive({
  malfunction: false,
  data: false,
})

export type SupportDialogName = keyof typeof dialogs

bus.on('dialogSupport.show', (subDialog?: SupportDialogName) => {
  if (subDialog) {
    dialogs[subDialog] = true
  } else {
    visible.value = true
  }
})

watch(
  () => dialogs,
  (newValue, oldValue) => {
    for (const [key, value] of Object.entries(newValue)) {
      if (value && !oldValue[key as keyof typeof oldValue]) {
        stores.apmEvents.track({ event: `open-${key}`, category: 'support' })
      }
    }
  },
  { deep: true },
)

function openExternalAndTrackAPM(url: string) {
  openExternal(url)
  stores.apmEvents.track({ event: 'click-'.concat(url), category: 'support' })
}

function openFeatureRequests() {
  window.open(`/canny/sso?companyID=${$env.canny.app_id}&redirect=${encodeURIComponent($env.canny.url_feature_requests)}`, '_blank', 'noopener')

  stores.apmEvents.track({ event: 'open-canny-feature-requests', category: 'support' })
}

function openStatusPage() {
  openExternal('statuspage')
  stores.apmEvents.track({ event: 'open-statuspage', category: 'support' })
}

function showLivechat() {
  livechat.show()
  visible.value = false
}
</script>

<style scoped>
.icon-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 600;
  font-size: var(--title-small);
  padding: 30px 15px 30px 15px;
  border-radius: 10px;
  transition: background-color var(--transition);
  cursor: pointer;

  &:hover {
    background-color: var(--color-brand-50);
  }

  & > .v-icon {
    margin-bottom: 15px;
  }
}

.v-table-key-value__row {
  cursor: pointer;

  :deep() .font-medium {
    font-weight: 400;
  }

  &:hover .v-btn {
    background-color: var(--color-brand-50);
  }
}
</style>
